import axios, { AxiosRequestConfig, InternalAxiosRequestConfig, AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';

// Create a utility function to handle navigation to login
const navigateToLogin = () => {
  // Clear token from localStorage
  localStorage.removeItem('token');
  
  // Use window.location for navigation since we can't use useNavigate hook outside of React components
  window.location.href = '/admin/login';
};

const BASE_URLS = {
  game: {
    prod: 'https://us-central1-taboo-game-emce.cloudfunctions.net/taboo',
    stage: 'https://us-central1-taboo-game-emce.cloudfunctions.net/tabooStage'
  },
  admin: {
    prod: 'https://us-central1-taboo-game-emce.cloudfunctions.net/tabooAdmin',
    stage: 'https://us-central1-taboo-game-emce.cloudfunctions.net/tabooAdminStage'
  }
};

// Function to check if user is admin
const isAdmin = () => {
  // This is a simple check - in a real app, you might want to use a more robust method
  const role = localStorage.getItem('userRole');
  return role === 'admin';
};

// Create separate axios instances for different APIs
const adminApi = axios.create({
  baseURL: BASE_URLS.admin.stage, // Always default to stage
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Accept-Language': 'tr', // Default language
  },
});

// Add request interceptor to include auth token and language
adminApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('token');
  const language = localStorage.getItem('language') || 'tr';

  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  // Allow individual requests to force a specific Accept-Language (e.g. reference card lookups)
  if (!(config as ForceLanguageConfig).forceLanguage) {
    config.headers.set('Accept-Language', language);
  }

  // Set the correct base URL based on user role and selection
  if (isAdmin()) {
    // Admin users can use their selected environment
    const selectedUrl = localStorage.getItem('adminBaseUrl');
    if (selectedUrl) {
      config.baseURL = selectedUrl;
    } else {
      config.baseURL = BASE_URLS.admin.stage;
    }
  } else {
    // Non-admin users always use stage
    config.baseURL = BASE_URLS.admin.stage;
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor for logging and handling 401 errors
adminApi.interceptors.response.use(
  (response) => {
    console.log('Admin API Response:', response);
    return response;
  },
  (error) => {
    console.error('Admin API Error:', error);
    
    // Check if the error is a 401 Unauthorized
    if (error.response && error.response.status === 401) {
      console.log('Unauthorized access detected, redirecting to login page');
      navigateToLogin();
    }
    
    return Promise.reject(error);
  }
);

const gameApi = axios.create({
  baseURL: BASE_URLS.game.stage, // Always default to stage
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Accept-Language': 'tr', // Default language
  },
});

// Add request interceptor to include language
gameApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const language = localStorage.getItem('language') || 'tr';
  
  // Set language header
  config.headers.set('Accept-Language', language);
  
  // Set the correct base URL based on user role and selection
  if (isAdmin()) {
    // Admin users can use their selected environment
    const selectedUrl = localStorage.getItem('gameBaseUrl');
    if (selectedUrl) {
      config.baseURL = selectedUrl;
    } else {
      config.baseURL = BASE_URLS.game.stage;
    }
  } else {
    // Non-admin users always use stage
    config.baseURL = BASE_URLS.game.stage;
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor for logging and handling 401 errors
gameApi.interceptors.response.use(
  (response) => {
    console.log('Game API Response:', response);
    return response;
  },
  (error) => {
    console.error('Game API Error:', error);
    
    // Check if the error is a 401 Unauthorized
    if (error.response && error.response.status === 401) {
      console.log('Unauthorized access detected, redirecting to login page');
      navigateToLogin();
    }
    
    return Promise.reject(error);
  }
);

interface ForceLanguageConfig extends AxiosRequestConfig {
  forceLanguage?: boolean;
}

interface CardInput {
  word?: string;
  forbiddenWords?: string[];
  difficulty?: 'easy' | 'medium' | 'hard';
  category1?: string;
  category2?: string;
  status?: 'active' | 'inactive';
  referenceId?: string;
}

export type MessageStatus = 'neutral' | 'winning' | 'loosing';

export interface MessageEntry {
  id: string;
  message: string;
  status: string;
}

export interface MessagesResponse {
  createdAt: string;
  updatedAt: string;
  [screenId: string]: MessageEntry[] | string;
}

export interface MessageInput {
  message: string;
  status: string;
}

export const MESSAGE_STATUSES: MessageStatus[] = ['neutral', 'winning', 'loosing'];

const METADATA_KEYS = new Set(['createdAt', 'updatedAt']);

export const getScreenIdsFromMessages = (data: MessagesResponse | null): string[] => {
  if (!data) return [];
  return Object.keys(data).filter(
    (key) => !METADATA_KEYS.has(key) && Array.isArray(data[key])
  );
};

export const getMessagesForScreen = (
  data: MessagesResponse | null,
  screenId: string
): MessageEntry[] => {
  if (!data || !screenId) return [];
  const entries = data[screenId];
  return Array.isArray(entries) ? entries : [];
};

export const getStatusesFromMessages = (messages: MessageEntry[]): string[] => {
  const statuses = new Set<string>(MESSAGE_STATUSES);
  messages.forEach((entry) => statuses.add(entry.status));
  return Array.from(statuses);
};

export const filterMessagesByStatus = (
  messages: MessageEntry[],
  status: string
): MessageEntry[] => {
  if (status === 'all') return messages;
  return messages.filter((entry) => entry.status === status);
};

export const formatScreenLabel = (screenId: string): string => {
  const name = screenId.startsWith('screen-') ? screenId.slice('screen-'.length) : screenId;
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export interface TeamNameEntry {
  id: string;
  teamName: string;
}

export interface TeamNameInput {
  teamName: string;
}

export interface TeamNamesResponse {
  createdAt: string;
  updatedAt: string;
  teamNames: TeamNameEntry[];
}

const API_URL = process.env.REACT_APP_API_URL || 'https://us-central1-taboo-game-emce.cloudfunctions.net/tabooAdmin';

const getToken = () => {
  return localStorage.getItem('token') || '';
};

// Admin API endpoints
export const adminEndpoints = {
  getDecks: () => adminApi.get('/decks'),
  getDeck: (id: string) => adminApi.get(`/deck?deckId=${id}`),
  createDeck: (data: any) => adminApi.post('/decks', data),
  updateDeck: (id: string, data: any) => adminApi.put(`/deck?deckId=${id}`, data),
  deleteDeck: (id: string) => adminApi.delete(`/deck?deckId=${id}`),
  apiTest: () => adminApi.get('/apitest'),
  getPackages: () => adminApi.get('/getPackageInfos'),
  getPackageDetails: (packageId: string) => adminApi.get(`/packages?packageIds=${packageId}`),
  createPackage: (packageId: string, description?: string) => {
    let url = `/package?packageId=${encodeURIComponent(packageId)}`;
    if (description) {
      url += `&description=${encodeURIComponent(description)}`;
    }
    return adminApi.post(url);
  },
  createCard: (packageId: string, data: CardInput | { cards: CardInput[] }) => {
    // If it's a single card, wrap it in a cards array
    const requestBody = Array.isArray(data) ? { cards: data } : 
                       'cards' in data ? data : 
                       { cards: [data] };
    return adminApi.post(`/cards?packageId=${packageId}`, requestBody);
  },
  updateCard: (packageId: string, cardId: string, data: Partial<CardInput>) => {
    return adminApi.put(`/card?packageId=${packageId}&id=${cardId}`, data);
  },
  getCardById: (id: string, packageId?: string) => {
    let url = `/card?id=${encodeURIComponent(id)}`;
    if (packageId) {
      url += `&packageId=${encodeURIComponent(packageId)}`;
    }
    return adminApi.get(url, {
      headers: { 'Accept-Language': 'tr' },
      forceLanguage: true,
    } as ForceLanguageConfig);
  },
  deleteCard: (packageId: string, cardId: string) => {
    return adminApi.delete(`/card?packageId=${packageId}&id=${cardId}`);
  },
  getAllWords: () => adminApi.get('/getAllWords'),
  getAllWordsCombined: () => adminApi.get('/getAllWordsCombined'),
  getMessages: () => adminApi.get('/messages'),
  createMessage: (screenId: string, data: MessageInput) =>
    adminApi.post(`/messages?screenId=${encodeURIComponent(screenId)}`, data),
  updateMessage: (screenId: string, id: string, data: MessageInput) =>
    adminApi.put(`/messages?screenId=${encodeURIComponent(screenId)}&id=${encodeURIComponent(id)}`, data),
  deleteMessage: (screenId: string, id: string) =>
    adminApi.delete(`/messages?screenId=${encodeURIComponent(screenId)}&id=${encodeURIComponent(id)}`),
  getTeamNames: () => adminApi.get('/teamNames'),
  createTeamName: (data: TeamNameInput) => adminApi.post('/teamNames', data),
  updateTeamName: (id: string, data: TeamNameInput) =>
    adminApi.put(`/teamNames?id=${encodeURIComponent(id)}`, data),
  deleteTeamName: (id: string) =>
    adminApi.delete(`/teamNames?id=${encodeURIComponent(id)}`),
};

// Game API endpoints
export const gameEndpoints = {
  getDecks: () => gameApi.get('/decks'),
  getDeck: (id: string) => gameApi.get(`/decks/${id}`),
  getRandomDeck: () => gameApi.get('/decks/random'),
};

// Deck management endpoints
export const getDecks = () => adminApi.get('/decks');
export const createDeck = (data: any) => adminApi.post('/decks', data);
export const updateDeck = (id: string, data: any) => adminApi.put(`/deck?deckId=${id}`, data);
export const deleteDeck = (id: string) => adminApi.delete(`/deck?deckId=${id}`);
export const apiTest = () => adminApi.get('/apitest');

// Game endpoints
export const getGameState = () => gameApi.get('/game/state');
export const startGame = (data: any) => gameApi.post('/game/start', data);
export const endGame = () => gameApi.post('/game/end');

// Language management
export const setLanguage = (language: string) => {
  localStorage.setItem('language', language);
  // Update headers for future requests
  adminApi.defaults.headers.common['Accept-Language'] = language;
  gameApi.defaults.headers.common['Accept-Language'] = language;
};

export const getLanguage = () => {
  return localStorage.getItem('language') || 'tr';
};

// Base URL management
export const setGameBaseUrl = (environment: 'prod' | 'stage') => {
  const baseUrl = BASE_URLS.game[environment];
  localStorage.setItem('gameBaseUrl', baseUrl);
  // We don't update the axios instance here anymore, it's handled by the interceptor
};

export const setAdminBaseUrl = (environment: 'prod' | 'stage') => {
  const baseUrl = BASE_URLS.admin[environment];
  localStorage.setItem('adminBaseUrl', baseUrl);
  // We don't update the axios instance here anymore, it's handled by the interceptor
};

export const getGameBaseUrl = () => {
  return isAdmin() ? (localStorage.getItem('gameBaseUrl') || BASE_URLS.game.stage) : BASE_URLS.game.stage;
};

export const getAdminBaseUrl = () => {
  return isAdmin() ? (localStorage.getItem('adminBaseUrl') || BASE_URLS.admin.stage) : BASE_URLS.admin.stage;
};

// New function to get the actual selected URL (for admin users)
export const getSelectedGameBaseUrl = () => {
  return localStorage.getItem('gameBaseUrl') || BASE_URLS.game.stage;
};

export const getSelectedAdminBaseUrl = () => {
  return localStorage.getItem('adminBaseUrl') || BASE_URLS.admin.stage;
};

export const getBaseUrls = () => BASE_URLS;

export const getPackageNames = async (): Promise<AxiosResponse<string[]>> => {
  try {
    const response = await adminApi.get('/getPackageNames');
    return response;
  } catch (error) {
    console.error('Error fetching package names:', error);
    throw error;
  }
};

// Config types
export interface ConfigResponse {
  version: number;
  messagesVersion: number;
  teamNamesVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface ConfigRequest {
  version: number;
  messagesVersion: number;
  teamNamesVersion: number;
}

// Config API endpoints
export const getConfig = async (): Promise<AxiosResponse<ConfigResponse>> => {
  try {
    const response = await adminApi.get('/config');
    return response;
  } catch (error) {
    console.error('Error fetching config:', error);
    throw error;
  }
};

export const updateConfig = async (data: ConfigRequest): Promise<AxiosResponse<ConfigResponse>> => {
  try {
    const response = await adminApi.post('/config', data);
    return response;
  } catch (error) {
    console.error('Error updating config:', error);
    throw error;
  }
};

export { adminApi, gameApi }; 